import React, { useState, useEffect, useRef } from "react";
import { Paragraph } from "components/Text";
import Pagination from "components/Pagination";
import { Button } from "components/Button/Button.styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint } from "@fortawesome/free-solid-svg-icons";
import DropdownButton from "components/Dropdown/DropdownButton";
import { ICalificacion, IGradesTableProps } from "./GradesTable.interface";
import { Icon } from "./GradesTable.styled";

const getCalificacion = (calificacion: string) => {
  const calificacionClasses: { [key: string]: string } = {
    A: "badge-teal",
    B: "badge-cyan",
    C: "badge-success",
    D: "badge-warning",
    E: "badge-danger",
  };

  const className =
    calificacionClasses[calificacion] || calificacionClasses.default;

  const isCalificacionValid = ["A", "B", "C", "D", "E"].includes(calificacion);

  return (
    <div className={`px-5 font-level-7 badge ${className}`}>
      <Paragraph className="m-0" color={isCalificacionValid ? "white" : null}>
        {calificacion &&
        calificacion !== "null" &&
        calificacion !== "NO APLICA" &&
        calificacion !== "0"
          ? calificacion
          : "N/A"}
      </Paragraph>
    </div>
  );
};

const GradesTable: React.FC<IGradesTableProps> = ({
  consultorData,
  children,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc" | "normal";
  }>({ key: "Periodo", direction: "desc" });
  const [originalData, setOriginalData] = useState<ICalificacion[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([
    "Periodo",
    "NotaObtenida",
    "ClasificacionObtenida",
    "Detalle",
  ]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOriginalData(consultorData?.Calificaciones);
  }, [consultorData?.Calificaciones]);

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" | "normal" = "asc";
    if (sortConfig && sortConfig.key === key) {
      if (sortConfig.direction === "asc") {
        direction = "desc";
      } else if (sortConfig.direction === "desc") {
        direction = "normal";
      }
    }
    setSortConfig({ key, direction });
  };

  const sortedCalificaciones = [...consultorData.Calificaciones].sort(
    (a, b) => {
      if (sortConfig !== null && sortConfig.direction !== "normal") {
        const { key, direction } = sortConfig;
        const order = direction === "asc" ? 1 : -1;
        const aValue = a[key as keyof ICalificacion];
        const bValue = b[key as keyof ICalificacion];
        if (typeof aValue === "string" && typeof bValue === "string") {
          return aValue.localeCompare(bValue) * order;
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return (aValue - bValue) * order;
        }
      }
      return 0;
    }
  );

  const displayedCalificaciones =
    sortConfig && sortConfig.direction === "normal"
      ? originalData
      : sortedCalificaciones;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = displayedCalificaciones.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const getSortIcon = (key: string) => {
    if (sortConfig && sortConfig.key === key) {
      if (sortConfig.direction === "asc") {
        return <Icon className="cl cl-m-arrow-up ml-auto mr-2" />;
      } else if (sortConfig.direction === "desc") {
        return <Icon className="cl cl-m-arrow-down ml-auto mr-2" />;
      }
    }
    return null;
  };

  const handlePrint = () => {
    const printContent = document.getElementById("grades-table")?.innerHTML;
    if (printContent) {
      const printWindow = window.open("", "", "width=800,height=600");
      if (printWindow) {
        printWindow.document.write(`
        <html>
          <head>
            <title>Proyectos consultor</title>
            <style>
              body {
                font-family: Arial, sans-serif;
              }
              table {
                width: 100%;
                border-collapse: collapse;
              }
              th, td {
                border: 1px solid black;
                padding: 8px;
                text-align: left;
              }
              th {
                background-color: #f2f2f2;
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
        </html>
      `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }
  };

  const toggleColumnVisibility = (column: string) => {
    setVisibleColumns((prev) => {
      if (prev.includes(column)) {
        return prev.filter((col) => col !== column);
      } else {
        return [
          "Periodo",
          "NotaObtenida",
          "ClasificacionObtenida",
          "Detalle",
        ].filter((col) => prev.includes(col) || col === column);
      }
    });
  };

  const handleItemsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setItemsPerPage(Number(event.target.value));
    setCurrentPage(1); // Reset to first page when limit changes
  };

  return (
    <div style={{ maxWidth: "100%", overflowX: "auto", position: "relative" }}>
      <div
        className="mb-3 align-items-center"
        style={{
          position: "sticky",
          left: 0,
          display: "flex",
          justifyContent: "space-between",
          padding: "8px 0",
          zIndex: 1000,
        }}
      >
        <div className="d-flex align-items-center">
          <Button className="btn btn-primary" onClick={handlePrint}>
            <FontAwesomeIcon icon={faPrint} />
          </Button>
          <div ref={dropdownRef}>
            <DropdownButton
              data-testid="column-visibility-button"
              columns={[
                "Periodo",
                "NotaObtenida",
                "ClasificacionObtenida",
                "Detalle",
              ]}
              visibleColumns={visibleColumns}
              toggleColumnVisibility={toggleColumnVisibility}
            />
          </div>
        </div>
        <div className="d-flex">
          <Paragraph>
            <label htmlFor="slctRecordsPerPage">Registros por página:</label>
          </Paragraph>
          <select
            className="form-control"
            id="slctRecordsPerPage"
            aria-label="Registros por página"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            {[10, 20, 50, 100].map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div id="grades-table">
        <table
          className="table table-striped table-bordered"
          style={{ maxWidth: "100%" }}
        >
          <thead className="thead">
            <tr>
              {visibleColumns.includes("Periodo") && (
                <th className="col-2" onClick={() => handleSort("Periodo")}>
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <Paragraph style={{ cursor: "pointer" }}>
                      <strong data-testid="table-header">Periodo</strong>
                    </Paragraph>
                    {getSortIcon("Periodo")}
                  </div>
                </th>
              )}
              {visibleColumns.includes("NotaObtenida") && (
                <th
                  className="col-2"
                  onClick={() => handleSort("NotaObtenida")}
                >
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <Paragraph style={{ cursor: "pointer" }}>
                      <strong>Nota Obtenida</strong>
                    </Paragraph>
                    {getSortIcon("NotaObtenida")}
                  </div>
                </th>
              )}
              {visibleColumns.includes("ClasificacionObtenida") && (
                <th
                  className="col-2"
                  onClick={() => handleSort("ClasificacionObtenida")}
                >
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <Paragraph style={{ cursor: "pointer" }}>
                      <strong>Clasificación Obtenida</strong>
                    </Paragraph>
                    {getSortIcon("ClasificacionObtenida")}
                  </div>
                </th>
              )}
              {visibleColumns.includes("Detalle") && (
                <th className="col-6" onClick={() => handleSort("Detalle")}>
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <Paragraph style={{ cursor: "pointer" }}>
                      <strong>Detalle</strong>
                    </Paragraph>
                    {getSortIcon("Detalle")}
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {currentItems.length > 0 ? (
              currentItems.map((calificacion, index) => (
                <tr key={index}>
                  {visibleColumns.includes("Periodo") && (
                    <td className="text-center">
                      <Paragraph>{calificacion.Periodo}</Paragraph>
                    </td>
                  )}
                  {visibleColumns.includes("NotaObtenida") && (
                    <td className="text-center">
                      <Paragraph>
                        {calificacion.NotaObtenida !== null &&
                        calificacion.NotaObtenida !== "null"
                          ? calificacion.NotaObtenida
                          : "N/A"}
                      </Paragraph>
                    </td>
                  )}
                  {visibleColumns.includes("ClasificacionObtenida") && (
                    <td className="text-center">
                      {getCalificacion(calificacion.ClasificacionObtenida)}
                    </td>
                  )}
                  {visibleColumns.includes("Detalle") && (
                    <td className="text-left">
                      <Paragraph>
                        {calificacion.Detalle !== null &&
                        calificacion.Detalle !== "null"
                          ? calificacion.Detalle
                          : "N/A"}
                      </Paragraph>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr className="text-center">
                <td colSpan={4}>
                  <Paragraph>Sin datos</Paragraph>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="text-center col-12">
        <div
          className="pagination-container text-center"
          style={{
            position: "sticky",
            left: 0,
            zIndex: 1000,
            width: "100%",
            padding: "8px 0",
            borderTop: "1px solid #ddd",
          }}
        >
          <Pagination
            className="mt-5"
            pagination={{
              page: currentPage,
              total: displayedCalificaciones.length,
              limit: itemsPerPage,
            }}
            setData={setCurrentPage}
          />
        </div>
      </div>
      {children}
    </div>
  );
};

export default GradesTable;
